import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import api from '../../services/api';
import { QUERY_KEYS } from '../../config/queryClient';
import { useTagSettings } from '../../context/TagSettingsContext';
import { useBrand } from '../../context/BrandContext';

const parseStoredDate = (value) => {
  if (!value) return null;

  const dateOnlyMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDateStr = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : parseStoredDate(value);
  if (!date || Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Hook para buscar estatísticas agregadas do Dashboard
 * Usa APIs otimizadas que retornam dados já processados pelo backend
 * 
 * @param {Object} params - Parâmetros
 * @returns {Object} Estatísticas e estados
 */
const useDashboardStats = ({ initialTimePeriod = '3d', filters = {} } = {}) => {
  const { tags: contextTags } = useTagSettings();
  const { activeBrand, brandVersion } = useBrand();

  const [timePeriod, setTimePeriod] = useState(() => {
    const saved = localStorage.getItem('dashboard_timePeriod');
    return saved || initialTimePeriod || '3d';
  });
  const [customDateRange, setCustomDateRange] = useState(() => {
    const savedStartDate = localStorage.getItem('dashboard_startDate');
    const savedEndDate = localStorage.getItem('dashboard_endDate');
    const savedDateType = localStorage.getItem('dashboard_dateType') || 'published';
    return savedStartDate || savedEndDate
      ? {
        startDate: parseStoredDate(savedStartDate),
        endDate: parseStoredDate(savedEndDate),
        dateType: savedDateType
      }
      : null;
  });

  // Salvar timePeriod no localStorage
  useEffect(() => {
    if (timePeriod) {
      localStorage.setItem('dashboard_timePeriod', timePeriod);
    }
  }, [timePeriod]);

  useEffect(() => {
    if (customDateRange?.startDate) {
      localStorage.setItem('dashboard_startDate', toDateStr(customDateRange.startDate));
    } else {
      localStorage.removeItem('dashboard_startDate');
    }
    if (customDateRange?.endDate) {
      localStorage.setItem('dashboard_endDate', toDateStr(customDateRange.endDate));
    } else {
      localStorage.removeItem('dashboard_endDate');
    }
    if (customDateRange?.dateType) {
      localStorage.setItem('dashboard_dateType', customDateRange.dateType);
    }
  }, [customDateRange]);

  // Helper para montar query params
  const getQueryParams = () => {
    const params = new URLSearchParams();
    if (timePeriod && timePeriod !== 'all') {
      params.append('timePeriod', timePeriod);
    }
    if (customDateRange?.startDate) params.append('startDate', toDateStr(customDateRange.startDate));
    if (customDateRange?.endDate) params.append('endDate', toDateStr(customDateRange.endDate));
    if (customDateRange?.dateType) params.append('dateType', customDateRange.dateType);
    if (filters.scope) params.append('scope', filters.scope);
    if (filters.category) params.append('category', filters.category);
    if (filters.uf) params.append('uf', filters.uf);
    if (filters.tags && filters.tags.length > 0) {
      params.append('tags', Array.isArray(filters.tags) ? filters.tags.join(',') : filters.tags);
    }
    if (filters.competitor) params.append('competitor', filters.competitor);
    if (filters.keyword) params.append('keyword', filters.keyword);
    return params.toString();
  };

  // Query para estatísticas gerais
  const statsQuery = useQuery({
    queryKey: [QUERY_KEYS.articles, 'dashboard-stats', brandVersion, { timePeriod, customDateRange, ...filters }],
    queryFn: async () => {
      try {
        const queryString = getQueryParams();
        console.log('📊 Buscando stats do Dashboard:', queryString);

        const response = await api.get(`/dashboard/stats?${queryString}`);
        return response.data.data;
      } catch (error) {
        console.error('❌ Erro ao buscar stats:', error);
        throw error;
      }
    },
    staleTime: 0, // Sempre buscar dados frescos ao trocar marca
    keepPreviousData: true,
  });

  // Query para comparativo de tags
  const tagsQuery = useQuery({
    queryKey: [QUERY_KEYS.articles, 'dashboard-tags', brandVersion, { timePeriod, customDateRange, ...filters }],
    queryFn: async () => {
      const queryString = getQueryParams();
      const response = await api.get(`/dashboard/tags-comparison?${queryString}`);
      return response.data.data;
    },
    staleTime: 0,
    keepPreviousData: true,
  });

  // Query para comparativo de coleções
  const collectionsQuery = useQuery({
    queryKey: [QUERY_KEYS.articles, 'dashboard-collections', brandVersion, { timePeriod, customDateRange, ...filters }],
    queryFn: async () => {
      const queryString = getQueryParams();
      const response = await api.get(`/dashboard/collections-comparison?${queryString}`);
      return response.data.data;
    },
    staleTime: 0,
    keepPreviousData: true,
  });

  const loading = statsQuery.isLoading || tagsQuery.isLoading || collectionsQuery.isLoading;
  const isFetching = statsQuery.isFetching || tagsQuery.isFetching || collectionsQuery.isFetching;

  return {
    stats: statsQuery.data || {},
    tagStats: tagsQuery.data || [],
    collectionStats: collectionsQuery.data || [],
    loading,
    isFetching,
    error: statsQuery.error || tagsQuery.error || collectionsQuery.error,
    tags: contextTags,
    timePeriod,
    setTimePeriod,
    customDateRange,
    setCustomDateRange,
  };
};

export default useDashboardStats;

